/**
 * Runtime configuration.
 *
 * The bundle is built once and deployed to every environment, so no endpoint,
 * pool id or origin is baked in at build time. The deploy writes a config.json
 * next to index.html and the app reads it during boot, before anything that
 * needs an endpoint renders.
 */

export interface AppConfig {
  /** AWS region of the user pool, e.g. 'us-east-1'. */
  region: string;
  userPoolId: string;
  userPoolClientId: string;
  /** Origin + prefix for the API, no trailing slash, e.g. '/api'. */
  apiBaseUrl: string;
  /** Origin + prefix for CDN media, no trailing slash. */
  mediaBaseUrl: string;
  /** Public origin of the site, used to preview a profile's public URL. */
  siteOrigin: string;
}

const CONFIG_URL = '/config.json';

let inflight: Promise<AppConfig> | null = null;
let loaded: AppConfig | null = null;

/**
 * Resolves the deployed configuration, fetching it at most once. The promise
 * (not the value) is memoised so concurrent callers during boot share a single
 * request instead of racing several.
 */
export function loadConfig(): Promise<AppConfig> {
  if (!inflight) {
    inflight = fetchConfig().catch((error: unknown) => {
      // Drop the memo on failure: a rejected promise cached forever would turn
      // one flaky request into a permanently broken app with no way to retry.
      inflight = null;
      throw error;
    });
  }
  return inflight;
}

/**
 * The already-loaded configuration, for the synchronous call sites (media URL
 * building, the API client) that run long after boot. Throws rather than
 * returning a half-configured object, because a silent empty baseUrl surfaces
 * later as an unexplained 404 against the wrong origin.
 */
export function getConfig(): AppConfig {
  if (!loaded) {
    throw new Error(
      'getConfig() was called before loadConfig() resolved. Anything that reads config must render inside the boot gate in App.tsx, or await loadConfig() first.',
    );
  }
  return loaded;
}

/**
 * Turns an asset's CDN-relative path into an absolute URL. Returns undefined
 * for a missing path so callers can pass `asset.posterPath` straight through
 * without producing a URL that points at the media root.
 */
export function mediaUrl(path: string | undefined): string | undefined {
  if (path === undefined) return undefined;
  const trimmed = path.trim();
  if (trimmed === '') return undefined;
  if (/^(?:https?:)?\/\//i.test(trimmed)) return trimmed;
  return `${getConfig().mediaBaseUrl}/${trimmed.replace(/^\/+/, '')}`;
}

async function fetchConfig(): Promise<AppConfig> {
  // 'no-cache' revalidates instead of trusting a cached copy: a stale config
  // points the app at a user pool or API that may no longer exist.
  const response = await fetch(CONFIG_URL, { cache: 'no-cache' });
  if (!response.ok) {
    throw new Error(
      `Could not load ${CONFIG_URL} (${response.status} ${response.statusText || 'error'}).`,
    );
  }

  let body: unknown;
  try {
    body = await response.json();
  } catch {
    throw new Error(`${CONFIG_URL} is not valid JSON.`);
  }

  const config = parseConfig(body);
  loaded = config;
  return config;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function parseConfig(body: unknown): AppConfig {
  if (!isRecord(body)) {
    throw new Error(`${CONFIG_URL} must contain a JSON object.`);
  }
  return {
    region: requireString(body, 'region'),
    userPoolId: requireString(body, 'userPoolId'),
    userPoolClientId: requireString(body, 'userPoolClientId'),
    apiBaseUrl: stripTrailingSlash(requireString(body, 'apiBaseUrl')),
    mediaBaseUrl: stripTrailingSlash(requireString(body, 'mediaBaseUrl')),
    siteOrigin: stripTrailingSlash(requireString(body, 'siteOrigin')),
  };
}

function requireString(source: Record<string, unknown>, key: string): string {
  const value = source[key];
  if (typeof value !== 'string' || value.trim() === '') {
    throw new Error(`${CONFIG_URL} is missing a value for "${key}".`);
  }
  return value.trim();
}

/** Every base URL is stored slash-free so joins never produce '//'. */
function stripTrailingSlash(value: string): string {
  return value.replace(/\/+$/, '');
}
